import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import type { IConfig } from '../../config/iConfig.js';
import type { PreparedContent } from '../../config/preparedContent.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';
import { TimingStage } from '../../base/timing/timingStage.js';
import type { LoaderNormal } from './loaderNormal.js';

export const lifecycleMethods = {
    load(this: LoaderNormal, config: IConfig, appSite: string, timing: PhaseTiming): boolean {
        try {
            const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Load called for appSite: ${appSite}, searchAppSites: ${searchAppSites}`, 'LoaderNormal');
            }

            this._config = config;
            this._appSite = appSite;
            const prepared = timing.measure(TimingPhase.Source, () => config.prepare([appSite]));
            if (!this._store.hasAppSite(this._appSite)) {
                const templates = (this.constructor as typeof LoaderNormal).loadTemplatesFromSingleAppSite(config, prepared, appSite, timing);
                this._store.addAppSite(this._appSite, templates);
            }

            if (searchAppSites) {
                const searchAppSitesArray = searchAppSites.split(',');
                for (let i = 0; i < searchAppSitesArray.length; i++) {
                    const searchAppSite = searchAppSitesArray[i].trim();
                    if (!searchAppSite) continue;
                    if (!this._store.hasAppSite(searchAppSite)) {
                        const searchTemplates = (this.constructor as typeof LoaderNormal).loadTemplatesFromSingleAppSite(config, prepared, searchAppSite, timing);
                        this._store.addAppSite(searchAppSite, searchTemplates);
                        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                            Logger.debug(`Cached searchAppSite '${searchAppSite}' with ${searchTemplates.size} templates`, 'LoaderNormal');
                        }
                    }
                }
            }

            timing.measure(TimingPhase.Dynamic, () => this._preloadDynamicAndGlobalData(config, prepared, appSite));
            Logger.info(`Loaded ${this._store.getTemplates(appSite).size} templates for ${appSite}`, 'LoaderNormal');
            return true;
        } catch (ex: unknown) {
            const message = ex instanceof Error ? ex.message : String(ex);
            Logger.error(`Failed to load templates: ${message}`, 'LoaderNormal');
            Logger.fallback("F-ENG2", `LoaderNormal load templates failed: ex=${message}`);
            throw ex;
        }
    },

    ensureAppSiteLoaded(this: LoaderNormal, appSite: string, timing: PhaseTiming): boolean {
        if (!appSite || !this._config) {
            return false;
        }
        const config = this._config;
        return timing.within('redirect', 'Normal', TimingStage.Load, () => this.load(config, appSite, timing));
    },

    _preloadDynamicAndGlobalData(this: LoaderNormal, config: IConfig, prepared: PreparedContent, appSite: string): void {
        Logger.flow(`event=preloadData phase=PerScenario engine=Normal appSite=${appSite}`, "Lifecycle");
        const allAppSites = [appSite];
        const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allAppSites.includes(trimmed)) {
                    allAppSites.push(trimmed);
                }
            }
        }

        for (const site of allAppSites) {
            const ddFolderJson = prepared.getFolderDataJson(site, 'dynamicdata');
            const ddAppSiteJson = prepared.getDataJson(site, 'dynamicdata');
            let ddConfig: DynamicConfigMap | null = null;
            const ddFolderConfig = DynamicConfigMap.parseDynamicConfig(ddFolderJson, 'Data');
            const ddAppSiteConfig = DynamicConfigMap.parseDynamicConfig(ddAppSiteJson, 'Data');
            if (ddFolderConfig) ddConfig = ddFolderConfig;
            if (ddAppSiteConfig) {
                if (!ddConfig) ddConfig = ddAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(ddConfig, ddAppSiteConfig);
            }
            this._dynamicDataConfigStore[site] = ddConfig;

            if (ddConfig) {
                for (const [, handlers] of ddConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicData\./i, 'dynamicdata.');
                        const paramJson = prepared.getDataJson(site, fileName) || prepared.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // CLIENT-DATA preparer bindings (dynamicclientdata.json) — parsed like dynamicdata.json but kept
            // in a SEPARATE store so client-data handlers never mix into the server IData path. "Data" legacy
            // key fallback is harmless here (bindings use unified IType/IParamFile + the DataSource set).
            const cdFolderJson = prepared.getFolderDataJson(site, 'dynamicclientdata');
            const cdAppSiteJson = prepared.getDataJson(site, 'dynamicclientdata');
            let cdConfig: DynamicConfigMap | null = null;
            const cdFolderConfig = DynamicConfigMap.parseDynamicConfig(cdFolderJson, 'Data');
            const cdAppSiteConfig = DynamicConfigMap.parseDynamicConfig(cdAppSiteJson, 'Data');
            if (cdFolderConfig) cdConfig = cdFolderConfig;
            if (cdAppSiteConfig) {
                if (!cdConfig) cdConfig = cdAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(cdConfig, cdAppSiteConfig);
            }
            this._dynamicClientDataConfigStore[site] = cdConfig;

            if (cdConfig) {
                for (const [, handlers] of cdConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicClientDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicClientData\./i, 'dynamicclientdata.');
                        const paramJson = prepared.getDataJson(site, fileName) || prepared.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicClientDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const dvFolderJson = prepared.getFolderDataJson(site, 'dynamicview');
            const dvAppSiteJson = prepared.getDataJson(site, 'dynamicview');
            let dvConfig: DynamicConfigMap | null = null;
            const dvFolderConfig = DynamicConfigMap.parseDynamicConfig(dvFolderJson, 'View');
            const dvAppSiteConfig = DynamicConfigMap.parseDynamicConfig(dvAppSiteJson, 'View');
            if (dvFolderConfig) dvConfig = dvFolderConfig;
            if (dvAppSiteConfig) {
                if (!dvConfig) dvConfig = dvAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(dvConfig, dvAppSiteConfig);
            }
            this._dynamicViewConfigStore[site] = dvConfig;

            if (dvConfig) {
                for (const [, handlers] of dvConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicViewParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicView\./i, 'dynamicview.');
                        const paramJson = prepared.getDataJson(site, fileName) || prepared.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicViewParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const daFolderJson = prepared.getFolderDataJson(site, 'dynamicaction');
            const daAppSiteJson = prepared.getDataJson(site, 'dynamicaction');
            let daConfig: DynamicConfigMap | null = null;
            const daFolderConfig = DynamicConfigMap.parseDynamicConfig(daFolderJson, 'Action');
            const daAppSiteConfig = DynamicConfigMap.parseDynamicConfig(daAppSiteJson, 'Action');
            if (daFolderConfig) daConfig = daFolderConfig;
            if (daAppSiteConfig) {
                if (!daConfig) daConfig = daAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(daConfig, daAppSiteConfig);
            }
            this._dynamicActionConfigStore[site] = daConfig;

            if (daConfig) {
                for (const [, handlers] of daConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicActionParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAction\./i, 'dynamicaction.');
                        const paramJson = prepared.getDataJson(site, fileName) || prepared.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicActionParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // DynamicAppSite handlers (AppSite-triggered). Mirrors C# LoaderNormal_Lifecycle: the
            // wwwroot-level wildcard ("*") layer (getRootJson) is least-specific; folder then appsite
            // override it. The config is keyed by AppSite name (and "*").
            const dsRootJson = prepared.getRootJson('dynamicappsite.json');
            const dsFolderJson = prepared.getFolderDataJson(site, 'dynamicappsite');
            const dsAppSiteJson = prepared.getDataJson(site, 'dynamicappsite');
            let dsConfig: DynamicConfigMap | null = null;
            const dsRootConfig = (dsRootJson && dsRootJson.size > 0) ? DynamicConfigMap.parseDynamicConfig(dsRootJson, 'AppSite') : null;
            const dsFolderConfig = DynamicConfigMap.parseDynamicConfig(dsFolderJson, 'AppSite');
            const dsAppSiteConfig = DynamicConfigMap.parseDynamicConfig(dsAppSiteJson, 'AppSite');
            if (dsRootConfig) dsConfig = dsRootConfig;
            if (dsFolderConfig) {
                if (!dsConfig) dsConfig = dsFolderConfig;
                else DynamicConfigMap.mergeDynamicConfig(dsConfig, dsFolderConfig);
            }
            if (dsAppSiteConfig) {
                if (!dsConfig) dsConfig = dsAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(dsConfig, dsAppSiteConfig);
            }
            this._dynamicAppSiteConfigStore[site] = dsConfig;

            if (dsConfig) {
                for (const [, handlers] of dsConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicAppSiteParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAppSite\./i, 'dynamicappsite.');
                        let paramJson = prepared.getDataJson(site, fileName) || prepared.getFolderDataJson(site, fileName);
                        if (!paramJson) {
                            const rootParam = prepared.getRootJson(fileName + '.json');
                            if (rootParam && rootParam.size > 0) paramJson = rootParam;
                        }
                        if (paramJson) this._dynamicAppSiteParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const globalDataJson = prepared.getDataJson(site, 'globaldata');
            this._globalDataStore.set(site, globalDataJson !== null ? JsonValue.object(globalDataJson) : JsonValue.null());

            const gvd = prepared.getDataJson(site, 'globalviewdata');
            if (gvd) {
                const resolvedGvd = new JsonObject();
                for (const componentName of gvd.keys()) {
                    const componentStoreKey = `${site}:${componentName.toLowerCase()}`;
                    const mappingValue = gvd.get(componentName)!;
                    let resolvedData: JsonObject | null = null;
                    if (mappingValue.isString) {
                        const mappingFileName = mappingValue.getString();
                        if (mappingFileName) {
                            resolvedData = prepared.getDataJson(site, mappingFileName);
                        }
                    } else if (mappingValue.isObject) {
                        resolvedData = mappingValue.getObject();
                    }
                    if (resolvedData) {
                        resolvedGvd.set(componentName, JsonValue.object(resolvedData));
                        if (!this._globalViewDataComponentStore.containsKey(componentStoreKey)) {
                            this._globalViewDataComponentStore.set(componentStoreKey, JsonValue.object(resolvedData));
                        }
                    }
                }
                this._globalViewDataStore.set(site, resolvedGvd.size > 0 ? JsonValue.object(resolvedGvd) : JsonValue.null());
            } else {
                this._globalViewDataStore.set(site, JsonValue.null());
            }
        }
    }
};
